import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateObligationOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateObligationOversightResultInput = {
  Id: string;
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL =
  (): UpdateObligationOversightResultMutation => ({
    update_obligation_oversight_result: {
      __typename: 'obligation_oversight_result_mutation_response',
      affected_rows: 1,
    },
  });

export const useUpdateObligationOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.updateObligationOversightResult.mutationOptions(
      {
        onSuccess: async () => {
          await Promise.all([
            // Apollo parity: evictField(cache, 'obligation_oversight_result')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.obligationOversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.pathKey(),
            }),
            // Apollo parity: evictField(cache, 'obligation_oversight_result_aggregate')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
            }),
            // Apollo parity: evictField(cache, 'oversight_assessment')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityRegister.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityCount.queryKey(),
            }),
          ]);
        },
      }
    ),
  });

  return {
    updateObligationOversightResult: async (
      variables: UpdateObligationOversightResultInput
    ): Promise<UpdateObligationOversightResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
